import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { subMonths } from "date-fns";
import { useEffect, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useLocation } from "react-router";
import { createSearchParams, Link, useSearchParams } from "react-router-dom";

import { ReactComponent as LockIcon } from "assets/icons/lock.svg";
import Box from "components/Box";
import Chip from "components/Chip";
import DateRangeField from "components/DateRangeField";
import Empty from "components/Empty";
import Pagination from "components/Pagination";
import RadioButtonGroup from "components/RadioButtonGroup";
import EnhancedSearchWithTypeField from "components/SearchWithTypeField";
import Select from "components/Select";
import SortButton from "components/SortButton";
import Table from "components/Table";
import TitleHeading from "components/TitleHeading";
import { useGetCourses, useGetQuestions } from "hooks/apis/question";
import useHandleParams from "hooks/useHandleParams";
import useMaskUserId from "hooks/useMaskUserId";
import format from "lib/format";
import { Question } from "types/types";
import {
  completedStatusOptions,
  onGoingQnaSearchTypeOptions,
  onGoingStatusOptions,
  qnaSearchTypeOptions,
  questionTypeOptions,
  subjectTypeOptions,
  transferSearchTypeOptions,
} from "utils/const";

type FormValues = {
  page: number;
  startDate: string;
  endDate: string;
  course: string;
  subjectType: string;
  questionType: string;
  questionStatus: string;
  searchType: string;
  searchText: string;
  sort?: string;
};

const STATUS: Record<string, string> = {
  "/qna/ongoing": "대기중",
  "/qna/completed": "답변 완료",
  "/qna/answered": "답변 완료",
  "/qna/transfer": "대기중",
};

const TITLES: Record<string, string> = {
  "/qna/transfer": "이관현황",
};

const TITLE_DESCRIPTIONS: Record<string, string> = {
  "/qna/transfer": "조교가 이관한 질문 화면입니다.",
};

const QnAListPage = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const queryClient = useQueryClient();
  const maskUserId = useMaskUserId();

  const { methods, searchRef } = useHandleParams<FormValues>({
    defaultValues: {
      startDate: format(subMonths(new Date(), 1), "yyyy-MM-dd"),
      endDate: format(new Date(), "yyyy-MM-dd"),
      course: "all",
      subjectType: "all",
      questionType: "all",
      questionStatus: "all",
      searchType: "title",
      searchText: "",
      page: 1,
    },
  });

  const [searchParams] = useSearchParams();

  const form = methods.watch();

  const { data, totalItems, currentPage, isFetching } = useGetQuestions({
    status: STATUS[pathname],
    size: 20,
    ...form,
    mine: pathname === "/qna/answered" ? 1 : 0,
    transfer: pathname === "/qna/transfer" ? 1 : 0,
  });
  const { data: courses } = useGetCourses({
    status: STATUS[pathname],
    startDate: form.startDate,
    endDate: form.endDate,
    mine: pathname === "/qna/answered" ? 1 : 0,
    transfer: pathname === "/qna/transfer" ? 1 : 0,
  });
  const coursesOptions = useMemo(
    () => [{ label: "강좌 전체", value: "all" }, ...(courses || [])],
    [courses],
  );

  useEffect(() => {
    if (pathname === "/qna/ongoing") {
      queryClient.invalidateQueries({ queryKey: ["questionCount"] });
    }
  }, [pathname]);

  useEffect(() => {
    if (courses?.length) {
      methods.setValue("course", methods.getValues("course"));
    }
  }, [courses]);

  const handleQuestionClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    question: Question,
  ) => {
    if (question.status === "작성중") {
      e.preventDefault();
    }
  };

  const SECTION_TITLES: Record<string, string> = {
    "/qna/ongoing": `대기중 (총 ${(totalItems ?? 0).toLocaleString()}건)`,
    "/qna/completed": "답변완료",
    "/qna/answered": "내 답변",
    "/qna/transfer": "대기중",
  };

  return (
    <FormProvider {...methods}>
      <main className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full">
        <TitleHeading
          className="mb-[0.625rem]"
          title={TITLES[pathname] ?? "QnA현황"}
          description={
            TITLE_DESCRIPTIONS[pathname] ?? "질문 및 답변 현황 화면입니다."
          }
        />
        <Box className="flex justify-between">
          <h3 className="text-18 font-medium">
            {SECTION_TITLES[pathname] ??
              `대기중 (총 ${(totalItems ?? 0).toLocaleString()}건)`}
          </h3>
        </Box>
        <Box
          backgroundColor="gray"
          className="py-[0.625rem] px-[1.875rem] flex gap-[3.125rem] mb-[0.625rem] 1500:!pr-[6.5rem]"
        >
          <div className="flex-1">
            <dl className="grid grid-cols-[7.5rem_1fr] 1600:grid-cols-[8.125rem_1fr] gap-y-[0.625rem]">
              <dt className="flex items-center">기간 검색</dt>
              <dd className="flex gap-[0.313rem]">
                <DateRangeField />
              </dd>
              <dt className="flex items-center">강좌</dt>
              <dd>
                <Select name="course" options={coursesOptions} />
              </dd>
              <dt className="flex items-center">과목 유형</dt>
              <dd>
                <Select name="subjectType" options={subjectTypeOptions} />
              </dd>
            </dl>
          </div>
          <div className="flex-1">
            <dl className="grid grid-cols-[7.5rem_1fr] 1600:grid-cols-[8.125rem_1fr] gap-y-[0.625rem]">
              <dt className="flex items-center">질문 유형</dt>
              <dd>
                <Select name="questionType" options={questionTypeOptions} />
              </dd>
              <dt className="flex items-center">질문 상태</dt>
              <dd className="flex gap-[0.313rem]">
                <RadioButtonGroup
                  name="questionStatus"
                  options={
                    pathname.includes("ongoing") ||
                    pathname.includes("transfer")
                      ? onGoingStatusOptions
                      : completedStatusOptions
                  }
                  defaultValue="all"
                />
              </dd>
              <dt className="flex items-center">검색 상세</dt>
              <dd className="flex gap-[0.313rem]">
                <EnhancedSearchWithTypeField
                  ref={searchRef}
                  options={
                    pathname.includes("ongoing")
                      ? onGoingQnaSearchTypeOptions
                      : pathname.includes("transfer")
                        ? transferSearchTypeOptions
                        : qnaSearchTypeOptions
                  }
                />
              </dd>
            </dl>
          </div>
        </Box>
        <Box className="!py-5 min-h-[calc(100%-18.75rem)] flex flex-col">
          <span className="text-16 font-medium text-secondary">
            총 {(totalItems ?? 0).toLocaleString()}개
          </span>
          <Table
            classNames={[
              "max-w-[7.5rem] min-w-[5.5rem] w-[7.98%]",
              "max-w-[7.5rem] min-w-[5rem] w-[7.98%]",
              "max-w-[9rem] min-w-[7.75rem] w-[7.98%]",
              "flex-1 px-4 1500:px-6 1600:px-8 1700:px-10 2000:px-[3.75rem] text-left",
              "max-w-[6.25rem] min-w-[5.25rem] w-[6.65%]",
              "max-w-[6.25rem] min-w-[4rem] w-[6.65%]",
              "max-w-[8rem] min-w-[7.5rem] w-[6.25%]",
              "max-w-[9.5rem] min-w-[7rem] w-[10.11%]",
              "max-w-[8rem] min-w-[7.5rem] w-[6.25%]",
              "max-w-[9.5rem] min-w-[7rem] w-[10.11%]",
            ]}
          >
            <thead>
              <tr>
                <th>번호</th>
                <th>
                  {["/qna/ongoing", "/qna/transfer"].includes(pathname)
                    ? "질문 상태"
                    : "답변 상태"}
                </th>
                <th>과목유형</th>
                <th>제목</th>
                <th>질문 유형</th>
                <th>활성화</th>
                <th>작성자</th>
                <th>
                  <SortButton name="created" defaultValue="created">
                    작성일
                  </SortButton>
                </th>
                {pathname !== "/qna/ongoing" && (
                  <>
                    <th>
                      {pathname === "/qna/transfer" ? "이관자" : "답변자"}
                    </th>
                    <th>
                      <SortButton
                        name={
                          pathname === "/qna/transfer"
                            ? "transferred"
                            : "answered"
                        }
                        defaultValue="created"
                      >
                        {pathname === "/qna/transfer" ? "이관일" : "답변일"}
                      </SortButton>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {data?.map((question, index) => (
                <tr
                  key={question.bNo}
                  className={clsx({
                    "bg-lightGray": question.status === "작성중",
                  })}
                >
                  <td>
                    {(
                      totalItems -
                      (index + 20 * (currentPage - 1))
                    ).toLocaleString()}
                  </td>
                  <td>
                    <Chip
                      color={
                        question.status === "대기중"
                          ? "red"
                          : question.status === "답변 완료"
                            ? "blue"
                            : question.status === "작성중"
                              ? "gray"
                              : "orange"
                      }
                    >
                      {question.status === "답변 완료"
                        ? "완료"
                        : question.status}
                    </Chip>
                  </td>
                  <td>{question.subjectType}</td>
                  <td>
                    <Link
                      onClick={(e) => handleQuestionClick(e, question)}
                      className={clsx(
                        "flex items-center duration-100",
                        question.status === "작성중"
                          ? "cursor-not-allowed"
                          : "hover:underline",
                      )}
                      to={{
                        pathname:
                          question.status === "작성중"
                            ? ""
                            : pathname === "/qna/transfer"
                              ? `/qna/transfer/${question.bNo}`
                              : `/qna/${question.bNo}`,
                        search: `?${createSearchParams(
                          JSON.parse(
                            JSON.stringify({
                              status: STATUS[pathname],
                              size: 20,
                              ...Object.fromEntries(searchParams.entries()),
                              mine: pathname === "/qna/answered" ? 1 : 0,
                              transfer: pathname === "/qna/transfer" ? 1 : 0,
                            }),
                          ),
                        )}`,
                      }}
                      // state={{
                      //   status: STATUS[pathname],
                      //   size: 20,
                      //   ...Object.fromEntries(searchParams.entries()),
                      //   mine: pathname === "/qna/answered" ? 1 : 0,
                      //   transfer: pathname === "/qna/transfer" ? 1 : 0,
                      // }}
                    >
                      {question.secret && (
                        <LockIcon className="size-6 shrink-0" />
                      )}
                      <p className="text-left line-clamp-1 break-all">
                        {question.title}
                      </p>
                    </Link>
                  </td>
                  <td>{question.questionType}</td>
                  <td>{question.enabled ? "Y" : "N"}</td>
                  <td>
                    <div className="flex flex-col items-center">
                      <span>{question.student?.studentName}</span>
                      <span className="text-12">
                        {maskUserId(question.student?.studentId)}
                      </span>
                    </div>
                  </td>
                  <td>{format(question.registrationDate, "yyyy/MM/dd")}</td>
                  {pathname !== "/qna/ongoing" && (
                    <>
                      <td>
                        {pathname === "/qna/transfer" ? (
                          <div className="flex flex-col items-center">
                            <span>{question.transfer?.user?.name} </span>
                            <span className="ßtext-12">
                              {question.transfer?.user?.userId}
                            </span>
                          </div>
                        ) : (
                          question.answers?.length && (
                            <div className="flex flex-col items-center">
                              <span>
                                {question.answers[0].user?.name ??
                                  question.answers[0].responderName}{" "}
                              </span>
                              <span className="text-12">
                                {maskUserId(question.answers[0].user?.userId) ||
                                  question.answers[0].responderId}
                              </span>
                            </div>
                          )
                        )}
                      </td>
                      <td>
                        {format(
                          question.answers?.[0]?.registrationDate ??
                            question.answers?.[0]?.createdAt ??
                            question.transfer?.createdAt,
                          "yyyy/MM/dd",
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          <Empty show={!isFetching && data && !data.length} />
          <Pagination total={totalItems} />
        </Box>
      </main>
    </FormProvider>
  );
};

export default QnAListPage;
